import { z } from "zod";
import { idSchema } from "@amber/contracts";
import { platformEntry } from "./platforms.js";
import { breakKindSchema, cadenceTierSchema, sourceKindSchema } from "./sources.js";
import { httpUrlSchema, platformSchema } from "./shared.js";

const keySchema = z.string().regex(/^[a-z][a-z0-9-]*$/);
const textSchema = z.string().trim().min(1);
const dateSchema = z.iso.date();
const languageSchema = z.string().regex(/^[a-z]{2,3}$/);

const evidenceSchema = z.strictObject({ url: httpUrlSchema, observed_on: dateSchema, note: textSchema });

const knownBreakSchema = z.strictObject({
  kind: breakKindSchema,
  from: dateSchema,
  to: dateSchema.optional(),
  reason: textSchema,
});

const validitySchema = { valid_from: dateSchema.optional(), valid_to: dateSchema.optional() };

const sourceFields = {
  key: keySchema,
  platform: platformSchema,
  kind: sourceKindSchema,
  title: textSchema,
  url: httpUrlSchema,
  platform_id: textSchema.optional(),
  speaker_id: z.string().regex(/^\d+$/).optional(),
  handle: textSchema.optional(),
  cadence_tier: cadenceTierSchema,
  evidence: z.array(evidenceSchema).min(1),
  known_breaks: z.array(knownBreakSchema).optional(),
};

export const startingOwnSourceSchema = z.strictObject({
  ...sourceFields,
  relationship: z.enum(["personal", "office", "organisation"]),
  ...validitySchema,
  main_own_channel: z.boolean(),
  required: z.boolean(),
});
export type StartingOwnSource = z.infer<typeof startingOwnSourceSchema>;

export const startingKnownAccountSchema = z.strictObject({
  platform: platformSchema,
  url: httpUrlSchema,
  handle: textSchema,
  relationship: z.enum([
    "personal",
    "office",
    "organisation",
    "programme_host",
    "guest_appearance",
    "publisher",
    "unverified_lead",
  ]),
  ...validitySchema,
  not_monitored_because: textSchema.optional(),
  evidence: z.array(evidenceSchema).min(1),
});
export type StartingKnownAccount = z.infer<typeof startingKnownAccountSchema>;

export const startingPersonSchema = z.strictObject({
  name: textSchema,
  profile_id: idSchema("prf"),
  wikidata: z.string().regex(/^Q\d+$/),
  languages: z.array(languageSchema).min(1),
  rationale: textSchema,
  exclusions: z.array(z.strictObject({ description: textSchema, reason: textSchema })),
  sources: z.array(startingOwnSourceSchema).min(1),
  known_accounts: z.array(startingKnownAccountSchema),
});
export type StartingPerson = z.infer<typeof startingPersonSchema>;

export const startingSharedSourceSchema = z.strictObject({
  ...sourceFields,
  publisher: textSchema,
  same_programme_as: keySchema.optional(),
  languages: z.array(languageSchema).min(1),
  reason: textSchema,
  appearances: z
    .array(z.strictObject({ person: textSchema, title: textSchema, url: httpUrlSchema, date: dateSchema.nullable() }))
    .min(1),
});
export type StartingSharedSource = z.infer<typeof startingSharedSourceSchema>;

function hostMatches(platform: string, url: string): boolean {
  const patterns = platformEntry(platform)?.url_patterns ?? [];
  const host = new URL(url).hostname.toLowerCase();
  return patterns.length === 0 || patterns.some((p) => host === p || host.endsWith(`.${p}`));
}

const startingListSchema = z.strictObject({
  version: z.literal(1),
  recorded_on: dateSchema,
  approval: textSchema,
  historical_window: z.literal("central"),
  people: z.array(startingPersonSchema).min(1),
  shared_sources: z.array(startingSharedSourceSchema),
});

type Path = (string | number)[];

interface ListCheck {
  readonly issue: (path: Path, message: string) => void;
  readonly recordedOn: string;
  readonly keys: Set<string>;
  readonly urls: Set<string>;
}

function checkEvidence(check: ListCheck, evidence: readonly { observed_on: string }[], path: Path): void {
  evidence.forEach((e, i) => {
    if (e.observed_on > check.recordedOn)
      check.issue([...path, "evidence", i, "observed_on"], "evidence is observed on or before the list's date");
  });
}

function checkValidity(
  check: ListCheck,
  v: { valid_from?: string | undefined; valid_to?: string | undefined },
  path: Path,
): void {
  if (v.valid_from !== undefined && v.valid_to !== undefined && v.valid_from >= v.valid_to)
    check.issue([...path, "valid_to"], "valid_to is after valid_from");
}

function checkSource(check: ListCheck, s: StartingOwnSource | StartingSharedSource, path: Path): void {
  const { issue } = check;
  const entry = platformEntry(s.platform);
  if (entry === undefined) {
    issue([...path, "platform"], `unknown platform ${s.platform}`);
    return;
  }
  if (entry.collection_state !== "collected")
    issue([...path, "platform"], `${entry.label} isn't collected; record it as a known account`);
  if (!hostMatches(s.platform, s.url)) issue([...path, "url"], `the URL is not on ${entry.label}`);
  if (check.keys.has(s.key)) issue([...path, "key"], `duplicate key ${s.key}`);
  check.keys.add(s.key);
  if (check.urls.has(s.url)) issue([...path, "url"], "another source has this URL");
  check.urls.add(s.url);
  checkEvidence(check, s.evidence, path);
}

function checkKnownAccount(check: ListCheck, a: StartingKnownAccount, path: Path): void {
  const entry = platformEntry(a.platform);
  if (entry === undefined) {
    check.issue([...path, "platform"], `unknown platform ${a.platform}`);
    return;
  }
  if (!hostMatches(a.platform, a.url)) check.issue([...path, "url"], `the URL is not on ${entry.label}`);
  if (entry.collection_state === "collected" && a.not_monitored_because === undefined) {
    check.issue(
      [...path, "not_monitored_because"],
      `${entry.label} is collected; a known account there says why it isn't monitored`,
    );
  }
  checkValidity(check, a, path);
  checkEvidence(check, a.evidence, path);
}

function checkPerson(check: ListCheck, person: StartingPerson, path: Path): void {
  const { issue } = check;
  if (person.sources.filter((s) => s.main_own_channel).length !== 1)
    issue([...path, "sources"], "a person has exactly one main own channel");
  person.sources.forEach((s, i) => {
    const sp = [...path, "sources", i];
    checkSource(check, s, sp);
    checkValidity(check, s, sp);
    if (s.required && s.kind !== "official_record")
      issue([...sp, "required"], "only official records are required routes (DI-111)");
    if ((s.required || s.main_own_channel) && s.cadence_tier !== "priority")
      issue([...sp, "cadence_tier"], "required routes and the main own channel default to Priority (DI-1)");
  });
  person.known_accounts.forEach((a, i) => {
    checkKnownAccount(check, a, [...path, "known_accounts", i]);
  });
}

function checkStartingList(list: z.infer<typeof startingListSchema>, ctx: z.RefinementCtx): void {
  const check: ListCheck = {
    issue: (path, message) => {
      ctx.addIssue({ code: "custom", path, message });
    },
    recordedOn: list.recorded_on,
    keys: new Set<string>(),
    urls: new Set<string>(),
  };
  const names = new Set(list.people.map((p) => p.name));
  list.people.forEach((person, p) => {
    checkPerson(check, person, ["people", p]);
  });
  list.shared_sources.forEach((s, i) => {
    const sp = ["shared_sources", i];
    checkSource(check, s, sp);
    s.appearances.forEach((a, j) => {
      if (!names.has(a.person)) check.issue([...sp, "appearances", j, "person"], `${a.person} is not in the list`);
    });
  });
  list.shared_sources.forEach((s, i) => {
    if (
      s.same_programme_as !== undefined &&
      !list.shared_sources.some((o) => o.key === s.same_programme_as && o.key !== s.key)
    ) {
      check.issue(
        ["shared_sources", i, "same_programme_as"],
        `no other shared source has the key ${s.same_programme_as}`,
      );
    }
  });
}

export const startingSourcesSchema = startingListSchema.superRefine(checkStartingList);
export type StartingSources = z.infer<typeof startingSourcesSchema>;
